import { useCallback, useEffect, useState } from "react";
import { API_URL, useAuth } from "./AuthContext";

// The API rebuilds both pages at most every 5 minutes, so polling faster shows nothing new
const POLL_MS = 5 * 60 * 1000;

// GET `path` on mount and every POLL_MS. A failed refresh keeps the last data on screen;
// `status` only reads "error" when nothing has loaded yet.
function usePlatformData(path) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  const load = useCallback(
    (signal) =>
      fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${token}` }, signal })
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
        .then((body) => {
          setData(body);
          setStatus("ready");
        })
        .catch((err) => {
          if (err.name === "AbortError") return;
          console.error(`Failed to load ${path}:`, err);
          setStatus((prev) => (prev === "ready" ? prev : "error"));
        }),
    [path, token]
  );

  useEffect(() => {
    let controller = new AbortController();
    load(controller.signal);
    const timer = setInterval(() => {
      controller.abort();
      controller = new AbortController();
      load(controller.signal);
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      controller.abort();
    };
  }, [load]);

  const reload = useCallback(() => {
    setStatus((prev) => (prev === "ready" ? prev : "loading"));
    return load();
  }, [load]);

  return { data, status, reload };
}

// Platform-wide figures and monthly charts for the Stats page (GET /api/stats)
export const useStats = () => usePlatformData("/api/stats");

// Ranked traders and summary figures for the Leaderboard page (GET /api/leaderboard)
export const useLeaderboard = () => usePlatformData("/api/leaderboard");
